import { WorkflowChildWorkPanelComponent } from './workflow-child-work-panel.component';
import { UiButtonDirective } from '@template-ui/directives';
import { WorkflowAutomatedActivitiesComponent } from './workflow-automated-activities.component';
import { WorkflowTimersPanelComponent } from './workflow-timers-panel.component';
import { createWorkflowBlockRegistry } from '@app/shared/workflow-blocks/workflow-blocks';
import { createWorkflowBlockPreviewTransport } from '@app/shared/workflow-blocks/workflow-block-preview';
import { WorkflowBlockContextService } from '@app/shared/services/workflow-block-context.service';
import { WorkflowPanelDraftsService } from '@app/shared/workflow-blocks/workflow-panel-drafts.service';
import {
  WorkflowBusinessStatusComponent,
  WorkflowBusinessStatusRead,
} from './workflow-business-status.component';
import { mapWorkflowRegistryDiagnosticError } from '@app/platform-admin/workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { InputConfig } from '@template-ui/types/ez-form.types';
import type { EzDocumentUploadOutcome } from '@template-ui/types/misc.types';
import { WorkflowDraftConfirmationService } from '@app/platform-admin/workflow-registry-diagnostics/workflow-draft-confirmation.service';
import { CommonModule } from '@angular/common';
import { effect } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { PageIdentityService } from '@app/shared/services/page-identity.service';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { EzButtonComponent } from '@template-ui/components/ez-button/ez-button.component';
import { EzPanelComponent } from '@template-ui/components/ez-panel/panel/panel.component';
import { EzTabGroupComponent } from '@template-ui/components/ez-tab-group/ez-tab-group.component';
import { WorkflowSummaryBarComponent } from '@app/shared/components/workflow-ledger/workflow-summary-bar.component';
import { WorkflowLedgerComponent } from '@app/shared/components/workflow-ledger/workflow-ledger.component';
import {
  WorkflowLedger,
  WorkflowLedgerViewState,
} from '@app/shared/components/workflow-ledger/workflow-ledger.models';
import { WorkflowLedgerApiService } from '@app/shared/services/workflow-ledger-api.service';
import { trustedWorkflowReturnRoute } from '@app/shared/workflow-navigation/workflow-return-route';
import { RoleService } from '@app/shared/services/roles.service';
import { WorkflowOwnerViewState } from '@app/platform-admin/workflow-registry-diagnostics/workflow-owner-view-state';
import { activeCaptureLookupIssues } from '@app/shared/workflow-commands/workflow-form-lookup';
import { isBlockOwnedCommand } from '@app/phms/workflow-blocks/domain-block-manifests';
import { toWorkflowLedger } from '@app/shared/services/workflow-ledger.adapter';
import { reviewHistoryNotes, reviewHistoryWorkSummary } from '@app/shared/workflow-commands/workflow-review-history';

import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  HostListener,
  Injector,
  runInInjectionContext,
  OnInit,
  OnChanges,
  DestroyRef,
  OnDestroy,
  inject,
  signal,
  computed,
} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink, UrlTree } from '@angular/router';
import { combineLatest, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppApis } from '@app/core/APIs/app-apis';
import { GeneratedCommandFormComponent } from '@app/shared/forms/generated-command-form.component';
import { createWorkflowReviewFormTerminology } from '@app/shared/terminology/workflow-review-form-terminology';
import { WORKFLOW_WORKSPACE_REVIEW_TERMINOLOGY } from '@app/shared/terminology/workflow-review-form.terminology';
import { WorkflowFormSubmissionsComponent } from '@app/shared/forms/workflow-form-submissions.component';
import {
  WorkflowDocumentsComponent,
  WorkflowDocumentRead,
} from './workflow-documents.component';
import { isUnsupportedCaptureForm } from '@app/platform-admin/workflow-registry-diagnostics/workflow-capture-form-validation';
import { captureFormContract } from '@app/shared/workflow-commands/workflow-capture-form';
import type {
  DetailConfigureTarget,
  DetailFieldOrder,
  DetailBlockMove,
} from './workflow-workspace-authoring';
import type {
  WorkflowFormSubmission,
  WorkflowCaptureForm,
} from '@app/shared/workflow-commands/workflow-capture-form';
import { WORKFLOW_CLIENT_COMMANDS } from '@app/shared/workflow-commands/installed-workflow-command-adapters';
import {
  workflowCommandIdentity,
  type WorkflowClientInteraction,
} from '@app/shared/workflow-commands/workflow-command-registry';
import {
  fieldPolicyKey,
  type WorkflowFieldPolicySource,
} from '@app/shared/workflow-commands/workflow-runtime-field-policy';
import { workflowCompleteCommandAdapter } from '@app/shared/workflow-commands/workflow-complete-command-adapter';
import {
  WorkflowReasonCaptureDialogService,
  type WorkflowReasonCaptureDialogHandle,
} from '@app/shared/components/workflow-reason-capture/workflow-reason-capture-dialog.service';
import {
  isWorkflowReasonCaptureContract,
  reasonCaptureIssues,
  reviewReasonCaptureContract,
  type WorkflowReasonCapturePresentation,
} from '@app/shared/workflow-commands/workflow-reason-capture';
import type {
  WorkflowCompletionChecklist,
  WorkflowDocumentUploadReceipt,
} from '@app/shared/workflow-commands/workflow-completion-requirements';
import { isCleanWorkflowDocumentUploadReceipt } from '@app/shared/workflow-commands/workflow-completion-requirements';
import {
  WorkflowCompletionChecklistComponent,
  uploadLimitLabel,
} from './workflow-completion-checklist.component';
import {
  validateGeneratedForm,
  serverFormIssues,
  FormIssues,
  GeneratedFormValues,
  GeneratedFormDefinition,
  visibleGeneratedFields,
} from '@template-ui/helpers/generated-form.helpers';
import {
  WorkspaceFrameComponent,
  WorkspaceToolbarComponent,
} from '@template-ui/blocks';
import { WorkspaceContextSession } from '@template-ui/blocks/context-reads';
import { WorkspaceBlockHostComponent } from '@template-ui/blocks/workspace-block-host.component';
import type { WorkspaceBlock, WorkspaceContext } from '@template-ui/blocks';
import type {
  WorkflowExecutionState,
  WorkflowAvailableAction,
} from '@app/platform-admin/workflow-registry-diagnostics/workflow-execution-panel.component';

interface Requirement {
  code: string;
  state: string;
  label?: string;
  reasonCode?: string;
  inputField?: string | null;
  source?: {
    type: string;
    recordId: number;
    revision: number;
    policyCode: string;
    policyVersion: string;
    definitionVersionId: number;
    configurationVersionId: number;
  };
}
export interface WorkspaceAction extends WorkflowAvailableAction {
  label: string;
  decisionRecordId: number | null;
  command: WorkflowAvailableAction['command'] & {
    key: { provider: string; kind: string; code: string; version: number };
    contractHash: string;
  };
  outcomes: (WorkflowAvailableAction['outcomes'][number] & {
    requirements: Requirement[];
  })[];
  explanation: {
    provider: string;
    version: number;
    status: string;
    message: string;
  };
}
export interface WorkflowPreviewInputChange {
  command: WorkspaceAction['command'];
  values: GeneratedFormValues;
}
export interface WorkflowWorkspace {
  stepProgress?: {
    occurrenceId: number;
    statusCode:
      | 'pending'
      | 'assigned'
      | 'in_progress'
      | 'completed'
      | 'bypassed';
    startMode: 'assigned_open' | 'manual';
    canStart: boolean;
    canStartOnOpen: boolean;
    eTag: string;
    reason?: string | null;
  } | null;
  businessStatus?: {
    workflow: WorkflowBusinessStatusRead | null;
    step: WorkflowBusinessStatusRead | null;
  };
  processName?: string | null;
  /** Optional navigation supplied by the authorized application adapter; never command authority. */
  sourceRecord?: { label: string; route: string } | null;
  formSubmissions?: WorkflowFormSubmission[];
  documentSubmissions?: WorkflowDocumentRead[];
  hasDocumentRequirements?: boolean;
  hasTimers?: boolean;
  timerReadVersion?: 1 | 2;
  hasChildLinks?: boolean;
  execution: WorkflowExecutionState & {
    availableActions: {
      occurrenceId: number | null;
      stepETag: string | null;
      evaluatedUtc: string;
      blockReasons: { code: string; message: string }[];
      actions: WorkspaceAction[];
    };
  };
  definitionVersionId: number;
  selectedOccurrenceId: number | null;
  nodeId: string | null;
  nodeTypeCode?: string | null;
  stepLabel: string | null;
  isCurrent: boolean;
  detail: {
    layout: string;
    contextPlacement: 'side' | 'below' | 'hidden';
    tabs?: { id: string; label: string }[];
    formTabId?: string;
    blocks: {
      instanceId: string;
      provider: string;
      code: string;
      version: number;
      settingsVersion: number;
      settings: unknown;
      region: 'main' | 'supporting';
      width: 'full' | 'half';
      tabId?: string;
    }[];
    mainInteraction?: {
      code: string;
      version: number;
      command: WorkspaceAction['command'];
    } | null;
  };
  context: WorkspaceContext;
  subject: { type: string | null; id: number | null };
}

@Component({
  selector: 'phms-workflow-workspace',
  standalone: true,
  imports: [
    UiButtonDirective,
    CommonModule,
    EzStandardStateComponent,
    EzButtonComponent,
    EzFormComponent,
    EzPanelComponent,
    EzTabGroupComponent,
    WorkflowSummaryBarComponent,
    WorkflowLedgerComponent,
    RouterLink,
    GeneratedCommandFormComponent,
    WorkflowFormSubmissionsComponent,
    WorkflowAutomatedActivitiesComponent,
    WorkflowTimersPanelComponent,
    WorkflowChildWorkPanelComponent,
    WorkflowDocumentsComponent,
    WorkflowCompletionChecklistComponent,
    WorkflowBusinessStatusComponent,
    WorkspaceFrameComponent,
    WorkspaceToolbarComponent,
    WorkspaceBlockHostComponent,
  ],
  templateUrl: './workflow-workspace.component.html',
  styleUrl: './workflow-workspace.component.scss',
  providers: [WorkflowPanelDraftsService],
})
export class WorkflowWorkspaceComponent
  implements OnDestroy, OnInit, OnChanges
{
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly http = inject(HttpClient);
  private readonly roles = inject(RoleService);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private workspaceAuthorityKey: string | undefined;
  private formTerminology: ReturnType<
    typeof createWorkflowReviewFormTerminology
  > | null = null;
  private readonly literalFormControls = Object.freeze({});
  formWording(definition: GeneratedFormDefinition) {
    return !this.previewMode && this.formTerminology
      ? this.formTerminology.project(definition)
      : { definition, controls: this.literalFormControls, diagnostics: [] };
  }
  private readonly commandAdapters = inject(WORKFLOW_CLIENT_COMMANDS);
  private readonly reasonDialogs = inject(WorkflowReasonCaptureDialogService);
  private reasonDialog: WorkflowReasonCaptureDialogHandle | null = null;
  private readonly injector = inject(Injector);
  private readonly ledgerApi = inject(WorkflowLedgerApiService);
  readonly panelDrafts = inject(WorkflowPanelDraftsService);
  readonly activePageTabId = signal<string | null>(null);
  private pageTabScope = '';
  private pageTabSelection = '';
  readonly pageTabs = computed(() => this.state()?.detail.tabs ?? []);
  readonly selectedPageTabId = computed(() => this.pageTabs().some(tab => tab.id === this.activePageTabId())
    ? this.activePageTabId() : this.pageTabs()[0]?.id ?? null);
  formOnActiveTab(): boolean {
    return !this.selectedPageTabId() || (this.state()?.detail.formTabId ?? this.pageTabs()[0]?.id) === this.selectedPageTabId();
  }
  selectPageTab(id: string | undefined): void {
    if (id && this.pageTabs().some(tab => tab.id === id)) {
      this.activePageTabId.set(id);
      if (this.isDetailAuthoring) this.detailTabChange.emit(id);
    }
  }
  hasActiveTabContent(): boolean {
    return !!(this.displayForm() && this.formOnActiveTab()) || this.mainBlocks.some(block => !this.selectedPageTabId() || block.tabId === this.selectedPageTabId());
  }
  readonly ledger = signal<WorkflowLedger | null>(null);
  readonly ledgerState = signal<WorkflowLedgerViewState>('loading');
  readonly historyExpanded = signal(false);
  private static nextId = 0;
  readonly historyId = `step-workspace-history-${++WorkflowWorkspaceComponent.nextId}`;
  get hasDocumentsBlock(): boolean {
    return (this.state()?.detail.blocks ?? []).some(
      (block) => block.provider === 'platform' && block.code === 'workflow.documents' && block.version === 1,
    );
  }
  @Input() previewLedger: WorkflowLedger | null = null;

  private readonly apis = inject(AppApis);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  backToQueue: UrlTree | null = null;
  returnTo: string | null = null;
  backToWorkflowOverview = false;
  backToProcessRuns = false;
  backToReferenceSearch = false;
  private historyRequested = false;
  private readonly destroy = inject(DestroyRef);
  readonly state = signal<WorkflowWorkspace | null>(null);
  readonly sourceRecordLink = computed(() => {
    if (this.previewMode || this.loading() || this.error()) return null;
    const source = this.state()?.sourceRecord;
    // Keep application-owned URLs intact, including record-mode query parameters. A bad or
    // unavailable adapter cannot turn this generic shell into an external navigation surface.
    if (
      !source ||
      typeof source.label !== 'string' ||
      !source.label.trim() ||
      source.label.length > 120 ||
      typeof source.route !== 'string' ||
      !/^\/(?!\/)[a-zA-Z0-9_./?=&%~-]+$/.test(source.route) ||
      /(?:^|\/)\.{1,2}(?:\/|\?|$)|%(?:2f|5c|2e|00)/i.test(source.route)
    )
      return null;
    try {
      const url = this.router.parseUrl(source.route);
      const returnTo = trustedWorkflowReturnRoute(this.returnTo);
      if (returnTo) url.queryParams['returnTo'] = returnTo;
      return { label: source.label, url };
    } catch {
      return null;
    }
  });
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly workflowStatusSaving = signal(false);
  readonly stepStatusSaving = signal(false);
  readonly startingWork = signal(false);
  readonly automatedActivitySaving = signal(false);
  readonly automatedActivityInputPending = signal(false);
  readonly childControlSaving = signal(false);
  readonly childControlInputPending = signal(false);
  readonly childInspectionKey = signal<string | null>(null);
  readonly visitControlInputPending = computed(
    () =>
      this.timerControlInputPending() ||
      this.childControlInputPending() ||
      this.panelDrafts.dirty(),
  );
  readonly timerControlSaving = signal(false);
  readonly timerControlInputPending = signal(false);
  readonly timerInspectionKey = signal<string | null>(null);
  get automatedWorkflowId(): number {
    return this.previewMode ? 0 : this.workflow;
  }
  get automatedOccurrenceId(): number | null {
    return this.state()?.selectedOccurrenceId ?? this.occurrence;
  }
  readonly stepStartError = signal('');
  readonly stepStartRefreshRequired = signal(false);
  readonly isSaving = computed(
    () =>
      this.saving() ||
      this.workflowStatusSaving() ||
      this.stepStatusSaving() ||
      this.startingWork() ||
      this.automatedActivitySaving() ||
      this.timerControlSaving() ||
      this.childControlSaving() ||
      this.panelDrafts.busy(),
  );
  constructor() {
    effect(() => {
      const authority = this.runtimeAuthorityKey();
      if (this.previewMode || this.workspaceAuthorityKey === undefined || authority === this.workspaceAuthorityKey) return;
      this.workspaceAuthorityKey = authority;
      this.invalidateChangedAuthority();
    });
    effect(() => {
      const data = this.state();
      if (this.previewMode) return;
      if (!data) { this.pageIdentity.clear(this); this.browserTitle.setTitle('PHMS'); return; }
      const label = [data.processName, data.stepLabel].filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).join(' · ') || 'PHMS';
      this.pageIdentity.set(this, this.router.url, label);
      this.browserTitle.setTitle(label);
    });
    this.panelDrafts.setRefreshHandler(() => {
      if (!this.previewMode) void this.load(true);
    });
    this.panelDrafts.setHostBlocker(
      () =>
        this.previewMode ||
        this.loading() ||
        this.saving() ||
        this.workflowStatusSaving() ||
        this.stepStatusSaving() ||
        this.startingWork() ||
        this.automatedActivitySaving() ||
        this.timerControlSaving() ||
        this.childControlSaving() ||
        this.timerControlInputPending() ||
        this.childControlInputPending() ||
        this.automatedActivityInputPending(),
    );
  }
  private readonly pageIdentity = inject(PageIdentityService);
  private readonly browserTitle = inject(Title);
  private readonly openedStepAttempts = new Set<string>();
  private startIntent: { signature: string; key: string } | null = null;
  private stepStartRequestNumber = 0;
  private stepStartAccessLost = false;
  private stepStartVisit: string | null = null;
  readonly error = signal('');
  readonly accessDenied = signal(false);
  readonly automationRecoveryAvailable = signal(false);
  readonly readState = computed(() =>
    this.loading()
      ? ('loading' as const)
      : !this.state() && this.error()
        ? this.accessDenied()
          ? ('forbidden' as const)
          : ('error' as const)
        : null,
  );
  readonly confirmation = signal('');
  readonly completionChecklist = signal<WorkflowCompletionChecklist | null>(
    null,
  );
  readonly checklistLoading = signal(false);
  readonly checklistError = signal('');
  readonly documentFiles = new Map<string, File>();
  readonly documentUploadOutcomes = new Map<string, EzDocumentUploadOutcome>();
  readonly uploadingDocument = signal<string | null>(null);
  private uploadAttempt = 0;
  private readonly documentIntents = new Map<
    string,
    { file: File; occurrenceId: number; eTag: string; key: string }
  >();
  private completionIntent: { signature: string; key: string } | null = null;
  private readonly taskIntents = new Map<
    string,
    { signature: string; key: string }
  >();
  private readonly taskDrafts = new Map<string, GeneratedFormValues>();
  private readonly taskForms = new Map<
    string,
    {
      form: GeneratedFormDefinition;
      label: string;
      initialValues: GeneratedFormValues;
      draftContextKey: string | null;
      fieldPolicyKey: string | null;
    }
  >();
  readonly reloadRequired = signal(false);
  readonly selectedCommand = signal('');
  readonly recoveredDraft = signal('');
  private editingOccurrence: number | null = null;
  values: GeneratedFormValues = {};
  issues: FormIssues = {};
  readonly registry = createWorkflowBlockRegistry();
  private readonly blockContext = inject(WorkflowBlockContextService);
  session?: WorkspaceContextSession;
  mainBlocks: WorkspaceBlock[] = [];
  supportingBlocks: WorkspaceBlock[] = [];
  private workflow = 0;
  private occurrence: number | null = null;
  private node: string | null = null;
  private generation = 0;
  private intent: { signature: string; key: string } | null = null;
  private readonly headers = new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  });
  @Input() previewMode = false;
  /** Synthetic input belongs to the preview owner, never the live task draft store. */
  @Input() previewInputs: Record<string, GeneratedFormValues> | null = null;
  @Input() previewReadOnly = false;
  @Output() previewInputChange = new EventEmitter<WorkflowPreviewInputChange>();
  @Input() previewData: WorkflowWorkspace | null = null;
  @Input() previewChecklist: WorkflowCompletionChecklist | null = null;
  @Input() detailAuthoring = false;
  @Input() detailEditingDisabled = false;
  @Input() detailSelection: DetailConfigureTarget | null = null;
  @Input() detailFieldIssues: Record<string, string> = {};
  @Output() detailConfigure = new EventEmitter<DetailConfigureTarget>();
  @Output() detailFieldOrderChange = new EventEmitter<DetailFieldOrder>();
  @Output() detailBlockMove = new EventEmitter<DetailBlockMove>();
  @Output() detailTabChange = new EventEmitter<string>();
  get isDetailAuthoring(): boolean {
    return this.previewMode && this.detailAuthoring;
  }
  get selectedField(): string | null {
    return this.detailSelection?.kind === 'field' &&
      this.detailSelection.command === this.selectedCommand()
      ? this.detailSelection.id
      : null;
  }
  get selectedBlock(): string | null {
    return this.detailSelection?.kind === 'block'
      ? this.detailSelection.id
      : null;
  }
  get selectedFieldIssues(): Record<string, string> {
    const prefix = `${this.selectedCommand()}:`;
    return Object.fromEntries(
      Object.entries(this.detailFieldIssues ?? {})
        .filter(([key]) => key.startsWith(prefix))
        .map(([key, value]) => [key.slice(prefix.length), value]),
    );
  }
  get formSelected(): boolean {
    return (
      this.detailSelection?.kind === 'form' &&
      this.detailSelection.command === this.selectedCommand()
    );
  }
  get actionSelected(): boolean {
    return (
      this.detailSelection?.kind === 'action' &&
      this.detailSelection.command === this.selectedCommand()
    );
  }
  configureAction(): void {
    this.configureDetail(
      this.action()
        ? { kind: 'action', command: this.selectedCommand() }
        : { kind: 'action-library' },
    );
  }
  get isCaptureForm(): boolean {
    const action = this.action();
    return (
      action?.command.key.provider === 'workflow' &&
      ['workflow.form.submit', 'workflow.form.save'].includes(
        action.command.key.code,
      ) &&
      action.command.key.version === 1 &&
      !!action.form?.captureForm
    );
  }
  configureDetail(target: DetailConfigureTarget): void {
    if (this.isDetailAuthoring) this.detailConfigure.emit(target);
  }
  addPageContent(region: 'main' | 'supporting'): void {
    if (
      !this.detailEditingDisabled &&
      this.allowedBlockRegions().includes(region)
    )
      this.configureDetail({ kind: 'add-block', region, ...(region === 'main' && this.selectedPageTabId() ? { tabId: this.selectedPageTabId()! } : {}) });
  }
  configureForm(): void {
    if (this.action())
      this.configureDetail({ kind: 'form', command: this.selectedCommand() });
  }
  configureField(id: string): void {
    if (this.displayForm()?.fields.some((field) => field.name === id))
      this.configureDetail({
        kind: 'field',
        command: this.selectedCommand(),
        id,
      });
  }
  reorderFields(fields: string[]): void {
    if (
      !this.isDetailAuthoring ||
      !this.isCaptureForm ||
      isUnsupportedCaptureForm(this.action()?.form?.captureForm)
    )
      return;
    const existing =
      this.action()?.form?.captureForm?.fields.map((field) => field.id) ?? [];
    if (
      !this.isDetailAuthoring ||
      !this.isCaptureForm ||
      fields.length !== existing.length ||
      new Set(fields).size !== fields.length ||
      !fields.every((id) => existing.includes(id)) ||
      fields.every((id, index) => existing[index] === id)
    )
      return;
    this.detailFieldOrderChange.emit({
      command: this.selectedCommand(),
      fields,
    });
  }
  allowedBlockRegions(): ('main' | 'supporting')[] {
    const detail = this.state()?.detail;
    return [
      'main',
      ...(detail?.contextPlacement !== 'hidden' ? ['supporting' as const] : []),
    ];
  }
  moveBlock(move: DetailBlockMove): void {
    const blocks = this.state()?.detail.blocks ?? [];
    if (
      !this.isDetailAuthoring ||
      !blocks.some((block) => block.instanceId === move.id) ||
      !this.allowedBlockRegions().includes(move.region) ||
      !Number.isInteger(move.index) ||
      move.index < 0 ||
      move.index >
        blocks.filter(
          (block) =>
            block.region === move.region && block.instanceId !== move.id,
        ).length
    )
      return;
    this.detailBlockMove.emit(move);
  }
  private readonly captureProjections = new WeakMap<
    WorkflowCaptureForm,
    GeneratedFormDefinition
  >();
  private readonly providedPreviewForms = new WeakMap<WorkspaceAction, {
    signature: string; form: GeneratedFormDefinition | null;
  }>();
  readonly emptyPreviewValues: GeneratedFormValues = {};
  /** A missing runtime lookup offer can still show its saved presentation in an inert preview. */
  displayOnlyPreviewForm(): GeneratedFormDefinition | null {
    if (!this.previewMode || this.form()) return null;
    const action = this.action();
    if (!action) return null;
    const interaction = this.state()?.detail.mainInteraction;
    const main = interaction && workflowCommandIdentity(interaction.command.key) === workflowCommandIdentity(action.command.key)
      ? interaction : null;
    const signature = JSON.stringify([action.command, action.form?.contract, action.form?.version,
      action.form?.inputSchemaHash, action.form?.configuration, action.form?.captureForm,
      action.form?.readOnlyFieldIds, action.outcomes, main]);
    const cached = this.providedPreviewForms.get(action);
    if (cached?.signature === signature) return cached.form;
    const form = this.commandAdapters.previewForm(action, main);
    this.providedPreviewForms.set(action, { signature, form });
    return form;
  }
  /** Keep unfinished schema fields visible for editing; this projection never authorizes a runtime command. */
  displayForm(): GeneratedFormDefinition | null {
    if (this.state()?.nodeTypeCode === 'automated_step') return null;
    if (this.selectedFormSaved() || this.selectedDecisionRecorded())
      return null;
    const resolved = this.form() ?? this.displayOnlyPreviewForm();
    if (!this.isDetailAuthoring || !this.isCaptureForm) return resolved;
    const capture = this.action()!.form!.captureForm!;
    if (isUnsupportedCaptureForm(capture)) return null;
    const cached = this.captureProjections.get(capture);
    if (cached) return cached;
    const projection = {
      ...captureFormContract,
      fields: capture.fields.map((field) => ({
        name: field.id,
        label: field.label || field.id || 'Untitled field',
        labelToken: '',
        required: field.required,
        nullable: !field.required,
        type: ['number', 'integer', 'boolean'].includes(field.type)
          ? field.type
          : 'string',
        control:
          field.type === 'boolean'
            ? 'checkbox'
            : field.type === 'integer'
              ? 'number'
              : field.type,
        options: field.options?.map((option) => ({
          ...option,
          labelToken: '',
        })),
        visibleWhen: field.visibleWhen
          ? {
              field: field.visibleWhen.fieldId,
              equals: field.visibleWhen.equals,
            }
          : undefined,
        errorCodes: [],
      })),
    };
    this.captureProjections.set(capture, projection);
    return projection;
  }
  isSaveOnlyForm(): boolean {
    const key = this.commandBinding()?.adapter.key;
    return (
      key?.provider === 'workflow' &&
      key.kind === 'command' &&
      key.code === 'workflow.form.save' &&
      key.version === 1
    );
  }
  selectedFormSaved(): boolean {
    if (this.previewMode || !this.isSaveOnlyForm()) return false;
    const state = this.state(),
      action = this.action(),
      form = action?.form?.captureForm;
    if (!state?.selectedOccurrenceId || !action || !form) return false;
    if (
      action.blockReasons.some(
        (reason) => reason.code === 'workflow.form.already_submitted',
      )
    )
      return true;
    // Both schemas come from the retained publication; do not hide a different form or visit.
    return !!state.formSubmissions?.some(
      (submission) =>
        submission.occurrenceId === state.selectedOccurrenceId &&
        submission.formId === form.id &&
        !!submission.schemaHash &&
        JSON.stringify(submission.form) === JSON.stringify(form),
    );
  }
  selectedDecisionRecorded(): boolean {
    const key = this.commandBinding()?.adapter.key;
    return (
      !this.previewMode &&
      key?.provider === 'workflow' &&
      key.kind === 'command' &&
      key.code === 'workflow.review.record' &&
      [1, 2].includes(key.version) &&
      !!this.action()?.blockReasons.some(
        (reason) => reason.code === 'workflow.review.already_recorded',
      )
    );
  }
  readonly taskPickerConfig = computed<InputConfig[]>(() => [
    {
      name: 'task',
      type: 'select',
      label: 'Task',
      width: 'fill',
      row: 1,
      options: this.actions().map((action) => ({
        value: workflowCommandIdentity(action.command.key),
        viewValue:
          action.label +
          (this.savedActionStatus(action)
            ? ` (${this.savedActionStatus(action)})`
            : action.enabled
              ? ''
              : ' (unavailable)'),
      })),
    },
  ]);
  readonly taskPickerValue = computed(() => ({ task: this.selectedCommand() }));
  private savedActionStatus(action: WorkspaceAction): string {
    const key = action.command.key;
    if (
      key.provider !== 'workflow' ||
      key.kind !== 'command'
    )
      return '';
    if (
      key.code === 'workflow.form.save' &&
      key.version === 1 &&
      action.blockReasons.some(
        (reason) => reason.code === 'workflow.form.already_submitted',
      )
    )
      return 'saved';
    if (
      key.code === 'workflow.review.record' &&
      [1, 2].includes(key.version ?? 0) &&
      action.blockReasons.some(
        (reason) => reason.code === 'workflow.review.already_recorded',
      )
    )
      return 'recorded';
    return '';
  }
  pickTask(value: Record<string, unknown>): void {
    const action = this.actions().find(
      (item) => workflowCommandIdentity(item.command.key) === value['task'],
    );
    if (action) {
      this.choose(action);
      if (this.isDetailAuthoring)
        this.configureDetail({
          kind: action.form ? 'form' : 'action',
          command: workflowCommandIdentity(action.command.key),
        });
    }
  }
  ngOnChanges(changes?: SimpleChanges): void {
    if (!this.previewMode) return;
    this.completionChecklist.set(this.previewChecklist);
    if (changes && !changes['previewData'] && !changes['previewMode']) {
      if (changes['previewLedger']) {
        this.ledger.set(this.previewLedger);
        this.ledgerState.set(this.previewLedger ? 'ready' : 'unavailable');
      }
      if (changes['previewInputs']) this.restorePreviewInputs();
      this.applyDetailSelection();
      return;
    }
    // A pending live request must never replace a newly selected synthetic context.
    ++this.generation;
    this.loading.set(false);
    this.error.set('');
    this.accessDenied.set(false);
    this.state.set(this.previewData);
    this.session?.dispose();
    if (this.previewData) this.configureBlocks(this.previewData, true);
    else {
      this.session = undefined;
      this.mainBlocks = [];
      this.supportingBlocks = [];
    }
    this.ledger.set(this.previewLedger);
    this.ledgerState.set(this.previewLedger ? 'ready' : 'unavailable');
    const preferred = this.preferredAction();
    this.selectedCommand.set(
      preferred ? workflowCommandIdentity(preferred.command.key) : '',
    );
    this.values = {};
    this.issues = {};
    this.taskDrafts.clear();
    this.documentFiles.clear();
    this.documentIntents.clear();
    this.documentUploadOutcomes.clear();
    this.uploadingDocument.set(null);
    this.taskForms.clear();
    this.taskIntents.clear();
    this.confirmation.set('');
    this.restorePreviewInputs();
    this.applyDetailSelection();
  }
  private restorePreviewInputs(): void {
    if (!this.previewMode || !this.previewInputs) return;
    this.taskDrafts.clear();
    for (const action of this.actions()) {
      const identity = workflowCommandIdentity(action.command.key);
      const values = this.previewInputs[identity];
      if (values) this.taskDrafts.set(identity, structuredClone(values));
    }
    const action = this.action();
    if (action) {
      this.values = { ...this.taskDrafts.get(this.selectedCommand()) };
      this.initializeTaskInput(action);
    }
  }
  private emitPreviewInput(): void {
    const action = this.action();
    if (this.previewMode && action)
      this.previewInputChange.emit({
        command: action.command,
        values: structuredClone(this.editableInput(this.values)),
      });
  }
  private applyDetailSelection(): void {
    const target = this.detailSelection;
    let tab: string | undefined;
    if (this.isDetailAuthoring && target?.kind === 'block') {
      const block = this.state()?.detail.blocks.find(block => block.instanceId === target.id);
      if (block?.region === 'main') tab = block.tabId ?? this.pageTabs()[0]?.id;
    }
    if (this.isDetailAuthoring && (target?.kind === 'form' || target?.kind === 'field'))
      tab = this.state()?.detail.formTabId ?? this.pageTabs()[0]?.id;
    const selection = JSON.stringify([target, tab]);
    if (selection !== this.pageTabSelection) { this.pageTabSelection = selection; this.selectPageTab(tab); }
    if (
      !this.isDetailAuthoring ||
      (target?.kind !== 'form' &&
        target?.kind !== 'field' &&
        target?.kind !== 'action')
    )
      return;
    const action = this.actions().find(
      (item) => workflowCommandIdentity(item.command.key) === target.command,
    );
    if (action) this.choose(action);
  }
  ngOnInit(): void {
    if (this.previewMode) return;
    this.formTerminology = runInInjectionContext(this.injector, () =>
      createWorkflowReviewFormTerminology(
        WORKFLOW_WORKSPACE_REVIEW_TERMINOLOGY,
      ),
    );
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(([params, query]) => {
        this.workflow = Number(params.get('workflow'));
        this.occurrence = params.has('occurrence')
          ? Number(params.get('occurrence'))
          : null;
        this.node = query.get('node');
        const returnTo = trustedWorkflowReturnRoute(query.get('returnTo'));
        this.backToProcessRuns =
          !!returnTo && /^\/workflows\/runs(?:\?[^#]*)?$/.test(returnTo);
        this.backToReferenceSearch = returnTo === '/workflows/find';
        this.backToWorkflowOverview = !!returnTo?.startsWith('/workflow-processes/') && !returnTo.includes('/dashboards/');
        this.returnTo = returnTo;
        this.backToQueue = this.returnTo
          ? this.router.parseUrl(this.returnTo)
          : this.router.parseUrl('/dashboard');
        this.values = {};
        this.issues = {};
        this.taskDrafts.clear();
        this.documentFiles.clear();
        this.documentIntents.clear();
        this.documentUploadOutcomes.clear();
        this.uploadingDocument.set(null);
        this.taskForms.clear();
        this.taskIntents.clear();
        this.confirmation.set('');
        this.selectedCommand.set('');
        this.historyRequested = query.get('history') === '1';
        this.historyExpanded.set(this.historyRequested);
        this.intent = null;
        this.recoveredDraft.set('');
        this.editingOccurrence = null;
        void this.load();
      });
  }
  private configureBlocks(state: WorkflowWorkspace, preview: boolean): void {
    const scope = `${state.context.workflow?.workflowInstanceId}/${state.selectedOccurrenceId}/${state.nodeId}`;
    if (scope !== this.pageTabScope) { this.activePageTabId.set(null); this.pageTabSelection = ''; this.pageTabScope = scope; }
    // Omit nullable position members: context v1 requires an exact occurrence whenever a node is supplied.
    const position = state.context.workflow!;
    const context: WorkspaceContext = {
      version: 1,
      domain: state.context.domain,
      workflow: {
        workflowInstanceId: position.workflowInstanceId,
        definitionVersionId: position.definitionVersionId,
        ...(state.selectedOccurrenceId
          ? {
              stepOccurrenceId: state.selectedOccurrenceId,
              nodeId: state.nodeId!,
              occurrenceNumber: position.occurrenceNumber,
            }
          : {}),
      },
    };
    this.session = preview
      ? new WorkspaceContextSession(
          context,
          createWorkflowBlockPreviewTransport(state, this.previewChecklist),
        )
      : this.blockContext.create(context);
    const blocks = state.detail.blocks.map((b) => ({
      instanceId: b.instanceId,
      block: { provider: b.provider, code: b.code, version: b.version },
      settingsVersion: b.settingsVersion,
      settings: b.settings ?? {},
      placement: { host: 'step-shell' as const, region: b.region },
      width: b.width,
      ...(b.region === 'main' && state.detail.tabs?.length ? { tabId: b.tabId ?? state.detail.tabs[0].id } : {}),
    }));
    this.mainBlocks = blocks.filter((b) => b.placement.region === 'main');
    this.supportingBlocks = blocks.filter(
      (b) => b.placement.region === 'supporting',
    );
  }
  async load(preservePanels = false): Promise<void> {
    const authority = this.runtimeAuthorityKey();
    if (!this.previewMode && this.workspaceAuthorityKey !== undefined && this.workspaceAuthorityKey !== authority) {
      this.workspaceAuthorityKey = authority;
      this.invalidateChangedAuthority();
    }
    const previous = preservePanels ? this.state() : null;
    this.reasonDialog?.close();
    this.reasonDialog = null;
    if (
      this.previewMode ||
      (!preservePanels && this.panelDrafts.dirty()) ||
      this.panelDrafts.busy()
    )
      return;
    // Preserve the last authorized schema while a refresh is in flight or temporarily unavailable.
    for (const action of this.actions())
      if (!this.taskForms.has(workflowCommandIdentity(action.command.key)))
        this.rememberTaskForm(action);
    const generation = ++this.generation;
    this.workspaceAuthorityKey = authority;
    this.loading.set(true);
    this.accessDenied.set(false);
    this.automationRecoveryAvailable.set(false);
    this.error.set('');
    this.completionChecklist.set(null);
    this.checklistLoading.set(false);
    this.checklistError.set('');
    if (previous) this.session?.invalidate('command-refresh-pending');
    if (!previous) {
      this.state.set(null);
      this.ledger.set(null);
      this.ledgerState.set('loading');
      this.session?.dispose();
      this.session = undefined;
    }
    if (
      !Number.isSafeInteger(this.workflow) ||
      this.workflow < 1 ||
      (this.occurrence !== null &&
        (!Number.isSafeInteger(this.occurrence) || this.occurrence < 1))
    ) {
      this.loading.set(false);
      this.error.set('This workflow link is invalid.');
      return;
    }
    try {
      const params: Record<string, string | number> = {};
      if (this.occurrence !== null) params['occurrenceId'] = this.occurrence;
      if (this.node) params['nodeId'] = this.node;
      let state = await firstValueFrom(
        this.http.get<WorkflowWorkspace>(
          this.apis.core.manual.platformWorkflowExecution(
            this.workflow,
            '/workspace',
          ),
          { params, headers: this.headers },
        ),
      );
      if (generation !== this.generation || authority !== this.runtimeAuthorityKey()) return;
      this.stepStartAccessLost = false;
      this.stepStartRefreshRequired.set(false);
      const visitKey = `${state.execution.workflowInstanceId}/${state.selectedOccurrenceId}`;
      if (this.stepStartVisit !== visitKey) {
        this.stepStartVisit = visitKey;
        this.stepStartError.set('');
        this.startIntent = null;
      }
      if (
        state.isCurrent &&
        !this.historyRequested &&
        state.nodeTypeCode !== 'automated_step' &&
        state.stepProgress?.canStartOnOpen &&
        !this.openedStepAttempts.has(visitKey)
      ) {
        this.openedStepAttempts.add(visitKey);
        if (await this.startWorkRequest(state, 'assigned_open', generation)) {
          if (generation !== this.generation || authority !== this.runtimeAuthorityKey()) return;
          state = await firstValueFrom(
            this.http.get<WorkflowWorkspace>(
              this.apis.core.manual.platformWorkflowExecution(
                this.workflow,
                '/workspace',
              ),
              { params, headers: this.headers },
            ),
          );
        }
        if (generation !== this.generation || authority !== this.runtimeAuthorityKey()) return;
        if (this.stepStartAccessLost) return;
      }
      if (state.stepProgress?.statusCode === 'in_progress')
        this.stepStartError.set('');
      this.pruneFieldPolicyDrafts(state);
      if (
        previous &&
        this.session &&
        previous.selectedOccurrenceId === state.selectedOccurrenceId &&
        previous.definitionVersionId === state.definitionVersionId &&
        JSON.stringify(previous.context) === JSON.stringify(state.context) &&
        JSON.stringify(previous.detail) === JSON.stringify(state.detail)
      ) {
        this.session.invalidate('command-confirmed');
      } else {
        this.session?.dispose();
        this.configureBlocks(state, false);
      }
      if (
        this.editingOccurrence !== null &&
        (this.editingOccurrence !== state.selectedOccurrenceId ||
          (!state.isCurrent && this.hasUnsavedInput()))
      ) {
        this.taskDrafts.set(
          this.selectedCommand(),
          this.editableInput(this.values),
        );
        const preserved = [...this.taskDrafts.entries()].filter(
          ([, values]) => Object.keys(values).length,
        );
        if (preserved.length)
          this.recoveredDraft.set(
            preserved
              .map(
                ([task, values]) =>
                  `${this.taskForms.get(task)?.label ?? task}\n${Object.entries(
                    values,
                  )
                    .map(
                      ([key, value]) =>
                        `${this.taskForms.get(task)?.form.fields.find((field) => field.name === key)?.label ?? key}: ${value ?? ''}`,
                    )
                    .join('\n')}`,
              )
              .join('\n\n'),
          );
        this.taskDrafts.clear();
        this.documentFiles.clear();
        this.documentIntents.clear();
        this.documentUploadOutcomes.clear();
        this.uploadingDocument.set(null);
        this.taskForms.clear();
        this.taskIntents.clear();
        this.values = {};
        this.issues = {};
        this.intent = null;
      }
      this.editingOccurrence = state.selectedOccurrenceId;
      this.state.set(state);
      void this.loadLedger(state.execution.workflowInstanceId, generation);
      void this.loadCompletionChecklist(generation);
      this.reloadRequired.set(false);
      if (
        !this.actions().some(
          (a) =>
            workflowCommandIdentity(a.command.key) === this.selectedCommand(),
        )
      ) {
        const next = this.preferredAction();
        if (next) this.choose(next);
        else {
          const identity = this.selectedCommand();
          const previous = this.taskForms.get(identity);
          const preserved = this.changedTaskInput(this.values, identity);
          if (Object.keys(preserved).length)
            this.recoveredDraft.set(
              [
                this.recoveredDraft(),
                `${previous?.label ?? identity}\n${Object.entries(preserved)
                  .map(
                    ([key, value]) =>
                      `${previous?.form.fields.find((field) => field.name === key)?.label ?? key}: ${value ?? ''}`,
                  )
                  .join('\n')}`,
              ]
                .filter(Boolean)
                .join('\n\n'),
            );
          this.taskDrafts.delete(identity);
          this.taskIntents.delete(identity);
          this.intent = null;
          this.selectedCommand.set('');
          this.values = {};
          this.issues = {};
        }
      }
      const activeAction = this.action();
      if (activeAction) this.initializeTaskInput(activeAction);
    } catch (error) {
      if (generation !== this.generation || authority !== this.runtimeAuthorityKey()) return;
      const denied =
        error instanceof HttpErrorResponse &&
        [401, 403, 404].includes(error.status);
      if (!previous || denied) this.state.set(null);
      else {
        this.reloadRequired.set(true);
        this.session?.invalidate('refresh');
      }
      if (
        error instanceof HttpErrorResponse &&
        [401, 403, 404].includes(error.status)
      ) {
        this.confirmation.set('');
        this.values = {};
        this.issues = {};
        this.intent = null;
        this.taskDrafts.clear();
        this.documentFiles.clear();
        this.documentIntents.clear();
        this.documentUploadOutcomes.clear();
        this.uploadingDocument.set(null);
        this.taskForms.clear();
        this.taskIntents.clear();
        this.recoveredDraft.set('');
      }
      this.accessDenied.set(
        error instanceof HttpErrorResponse && [401, 403].includes(error.status),
      );
      this.automationRecoveryAvailable.set(!denied && !this.state());
      this.error.set(
        this.accessDenied()
          ? 'You do not have access to this workspace. Check access to try again.'
          : 'This workspace could not be loaded. Retry to check its current state.',
      );
    } finally {
      if (generation === this.generation) this.loading.set(false);
    }
  }
  private async loadLedger(
    workflowId: number,
    generation: number,
  ): Promise<void> {
    try {
      const result = await firstValueFrom(this.ledgerApi.getLedger(workflowId));
      if (generation !== this.generation || !this.state()) return;
      this.ledger.set(toWorkflowLedger(result));
      this.ledgerState.set('ready');
    } catch {
      if (generation !== this.generation || !this.state()) return;
      this.ledger.set(null);
      this.ledgerState.set('error');
    }
  }
  readonly reviewHistoryNotes = reviewHistoryNotes;
  readonly reviewHistoryWorkSummary = reviewHistoryWorkSummary;
  selectedOccurrence() {
    const data = this.state();
    return data?.execution.occurrences.find(
      (s) => s.id === data.selectedOccurrenceId,
    );
  }
  selectedReviews() {
    const occurrence = this.selectedOccurrence();
    return (
      this.state()?.execution.reviewHistory?.filter(
        (review) =>
          !this.state()?.selectedOccurrenceId ||
          (review.stepCode === occurrence?.stepCode &&
            review.occurrenceNumber === occurrence?.occurrenceNumber),
      ) ?? []
    );
  }
  isCompleted(): boolean {
    return this.state()?.execution.statusCode === 'completed';
  }
  isClosed(): boolean {
    return ['completed', 'cancelled'].includes(
      this.state()?.execution.statusCode ?? '',
    );
  }
  isEditableVisit(): boolean {
    if (this.state()?.nodeTypeCode === 'automated_step') return false;
    return !!this.state()?.isCurrent && !this.isClosed();
  }
  stepProgressLabel(): string {
    return this.displayStatus(
      this.state()?.stepProgress?.statusCode ||
        this.selectedOccurrence()?.statusCode ||
        this.state()?.execution.statusCode ||
        '',
    );
  }
  isWaitingForStart(): boolean {
    return ['pending', 'assigned'].includes(
      this.state()?.stepProgress?.statusCode ?? '',
    );
  }
  canStartWork(): boolean {
    if (this.state()?.nodeTypeCode === 'automated_step') return false;
    const state = this.state();
    return (
      !this.previewMode &&
      !!state?.isCurrent &&
      this.isWaitingForStart() &&
      !!state.stepProgress?.canStart &&
      state.stepProgress.occurrenceId === state.selectedOccurrenceId &&
      !this.loading() &&
      !this.isSaving() &&
      !this.hasUnsavedInput() &&
      !this.stepStartRefreshRequired()
    );
  }
  async startWork(): Promise<void> {
    const state = this.state();
    if (!state || !this.canStartWork()) return;
    this.openedStepAttempts.add(
      `${state.execution.workflowInstanceId}/${state.selectedOccurrenceId}`,
    );
    if (await this.startWorkRequest(state, 'manual', this.generation))
      await this.load();
  }
  private async startWorkRequest(
    state: WorkflowWorkspace,
    source: 'assigned_open' | 'manual',
    generation: number,
  ): Promise<boolean> {
    const progress = state.stepProgress;
    const step = state.execution.occurrences.find(
      (item) => item.id === state.selectedOccurrenceId,
    );
    if (
      this.previewMode ||
      !state.isCurrent ||
      !progress ||
      !step ||
      progress.occurrenceId !== step.id ||
      !progress.eTag ||
      (source === 'assigned_open'
        ? !progress.canStartOnOpen
        : !progress.canStart)
    )
      return false;
    const signature = JSON.stringify([
      state.execution.workflowInstanceId,
      step.id,
      progress.eTag,
      source,
    ]);
    if (this.startIntent?.signature !== signature)
      this.startIntent = { signature, key: crypto.randomUUID() };
    this.startingWork.set(true);
    const requestNumber = ++this.stepStartRequestNumber;
    this.stepStartError.set('');
    try {
      const receipt = await firstValueFrom(
        this.http.post<{
          workflowInstanceId: number;
          workflowStepInstanceId: number;
          statusCode: string;
          eTag: string;
        }>(
          this.apis.core.manual.platformWorkflowExecution(
            state.execution.workflowInstanceId,
            `/steps/${encodeURIComponent(step.stepCode)}/start`,
          ),
          { occurrenceId: step.id, source },
          {
            headers: this.headers
              .set('If-Match', progress.eTag)
              .set('Idempotency-Key', this.startIntent.key),
          },
        ),
      );
      if (generation !== this.generation) return false;
      if (
        receipt?.workflowInstanceId !== state.execution.workflowInstanceId ||
        receipt.workflowStepInstanceId !== step.id ||
        receipt.statusCode !== 'in_progress' ||
        typeof receipt.eTag !== 'string' ||
        !receipt.eTag.trim()
      )
        throw new Error('Unconfirmed step start');
      this.stepStartRefreshRequired.set(false);
      return true;
    } catch (error) {
      if (generation !== this.generation) return false;
      if (
        error instanceof HttpErrorResponse &&
        [400, 401, 403, 404, 409, 412].includes(error.status)
      ) {
        const mapped = mapWorkflowRegistryDiagnosticError(error);
        this.stepStartError.set(
          mapped.messages.join(' ') ||
            'This step could not be started. Refresh to check its current assignment and progress.',
        );
        if ([401, 403, 404].includes(error.status)) {
          this.reasonDialog?.close(true);
          this.reasonDialog = null;
          this.stepStartAccessLost = true;
          this.accessDenied.set([401, 403].includes(error.status));
          this.state.set(null);
          this.error.set(
            'Access to start this work could not be confirmed. Refresh the process to continue.',
          );
        }
      } else {
        this.stepStartError.set(
          'Starting this step could not be confirmed. Refresh to check its progress before trying again.',
        );
      }
      this.stepStartRefreshRequired.set(true);
      return false;
    } finally {
      if (requestNumber === this.stepStartRequestNumber)
        this.startingWork.set(false);
    }
  }
  visitQueryParams(): Record<string, string | null> {
    return {
      returnTo: this.returnTo,
      history: this.historyRequested ? '1' : null,
    };
  }
  backLinkLabel(): string {
    return this.backToReferenceSearch
      ? 'Back to reference search'
      : this.backToProcessRuns
        ? 'Back to process runs'
        : 'Back to dashboard';
  }
  recordedWorkTitle(): string {
    const state = this.state();
    if (!state?.subject.type && this.isClosed())
      return this.isCompleted() ? 'Process completed' : 'Process cancelled';
    return this.isCompleted() ? 'Workflow completed' : 'Recorded visit';
  }
  recordedWorkDescription(): string {
    if (!this.state()?.subject.type && this.isClosed())
      return `This process run is ${this.isCompleted() ? 'complete' : 'cancelled'}. Its recorded visits are read-only.`;
    return this.isCompleted()
      ? 'This workflow is complete. Recorded work is available for reference.'
      : 'This visit is read-only. The workflow summary shows its current position.';
  }
  hasCurrentWork(): boolean {
    return !this.isClosed() && !!this.state()?.execution.activeOccurrenceId;
  }
  private fieldPolicySource(
    state = this.state(),
  ): WorkflowFieldPolicySource | undefined {
    return state?.selectedOccurrenceId
      ? {
          workflowInstanceId: state.execution.workflowInstanceId,
          occurrenceId: state.selectedOccurrenceId,
          configurationVersionId: state.execution.configurationVersionId,
          definitionVersionId: state.definitionVersionId,
          definitionHash: state.execution.canonicalContentHash,
        }
      : undefined;
  }
  private resolveCommand(
    action: WorkspaceAction,
    interaction?: WorkflowClientInteraction | null,
  ) {
    if (!this.previewMode && this.workspaceAuthorityKey !== undefined && this.workspaceAuthorityKey !== this.runtimeAuthorityKey()) return null;
    return this.commandAdapters.resolve(
      action,
      interaction,
      this.fieldPolicySource(),
    );
  }
  // Policy changes cannot turn old restricted input into a recovery message or another task's draft.
  private pruneFieldPolicyDrafts(state: WorkflowWorkspace): void {
    for (const [identity, previous] of this.taskForms) {
      const next = (
        state.execution.availableActions?.actions as
          | WorkspaceAction[]
          | undefined
      )?.find(
        (action) => workflowCommandIdentity(action.command.key) === identity,
      );
      const key = fieldPolicyKey(next?.form?.fieldPolicy);
      const nextKey =
        key &&
        next &&
        !this.commandAdapters.resolve(next, null, this.fieldPolicySource(state))
          ? `unavailable:${key}`
          : key;
      if (previous.fieldPolicyKey === nextKey) continue;
      if (!previous.fieldPolicyKey && !nextKey) continue;
      this.taskDrafts.delete(identity);
      this.taskIntents.delete(identity);
      this.recoveredDraft.set('');
      if (identity === this.selectedCommand()) {
        this.values = {};
        this.issues = {};
        this.intent = null;
      }
      this.taskForms.delete(identity);
    }
  }
  private editableInput(
    values: GeneratedFormValues,
    identity = this.selectedCommand(),
  ): GeneratedFormValues {
    const action = this.actions().find(
      (item) => workflowCommandIdentity(item.command.key) === identity,
    );
    const form =
      this.taskForms.get(identity)?.form ??
      (action ? this.rememberTaskForm(action) : null);
    return this.commandAdapters.editableInput(identity, values, form);
  }
  private rememberTaskForm(
    action: WorkspaceAction,
  ): GeneratedFormDefinition | null {
    const identity = workflowCommandIdentity(action.command.key);
    const binding = this.resolveCommand(action);
    const form = binding?.form ?? null;
    if (form)
      this.taskForms.set(identity, {
        form,
        label: action.form?.captureForm?.title || action.label,
        initialValues: this.commandAdapters.initialInput(action, {}),
        draftContextKey: binding?.adapter.draftContextKey?.(action) ?? null,
        fieldPolicyKey: fieldPolicyKey(action.form?.fieldPolicy),
      });
    return form;
  }
  private changedTaskInput(
    values: GeneratedFormValues,
    identity: string,
  ): GeneratedFormValues {
    const saved = this.taskForms.get(identity);
    const normalize = (name: string, value: unknown): string => {
      if (value === null || value === undefined || value === '') return '';
      if (
        saved?.form.fields.find((field) => field.name === name)
          ?.dateSemantics === 'utc-instant'
      ) {
        const time =
          value instanceof Date
            ? value.getTime()
            : typeof value === 'string'
              ? Date.parse(value)
              : Number.NaN;
        if (Number.isFinite(time)) return new Date(time).toISOString();
      }
      return JSON.stringify(value);
    };
    return Object.fromEntries(
      Object.entries(this.editableInput(values, identity)).filter(
        ([name, value]) =>
          normalize(name, value) !==
          normalize(name, saved?.initialValues[name]),
      ),
    );
  }
  private initializeTaskInput(action: WorkspaceAction): void {
    const identity = workflowCommandIdentity(action.command.key);
    const previous = this.taskForms.get(identity);
    const binding = this.resolveCommand(action);
    if (!binding) return;
    const nextContext = binding.adapter.draftContextKey?.(action) ?? null;
    const changed = previous
      ? this.changedTaskInput(this.values, identity)
      : this.values;
    if (
      previous &&
      (previous.draftContextKey !== null || nextContext !== null) &&
      previous.draftContextKey !== nextContext
    ) {
      if (Object.keys(changed).length) {
        this.recoveredDraft.set(
          [
            this.recoveredDraft(),
            `${previous.label} — the linked record changed. Previous input was kept separately.\n${Object.entries(
              changed,
            )
              .map(
                ([name, value]) =>
                  `${previous.form.fields.find((field) => field.name === name)?.label ?? name}: ${value ?? ''}`,
              )
              .join('\n')}`,
          ]
            .filter(Boolean)
            .join('\n\n'),
        );
      }
      this.values = {};
      this.taskDrafts.delete(identity);
      this.taskIntents.delete(identity);
      this.intent = null;
    } else this.values = changed;
    this.rememberTaskForm(action);
    this.values = this.commandAdapters.initialInput(action, this.values);
  }
  hasUnsavedInput(): boolean {
    if (this.previewMode) return false;
    const meaningful = (values: GeneratedFormValues, identity: string) =>
      Object.keys(this.changedTaskInput(values, identity)).length > 0;
    return (
      this.automatedActivityInputPending() ||
      this.visitControlInputPending() ||
      this.documentFiles.size > 0 ||
      meaningful(this.values, this.selectedCommand()) ||
      !!this.recoveredDraft() ||
      [...this.taskDrafts.entries()].some(
        ([key, values]) =>
          key !== this.selectedCommand() && meaningful(values, key),
      )
    );
  }
  confirmNavigation(): boolean | Promise<boolean> {
    if (this.previewMode) return true;
    if (this.isSaving()) return false;
    if (!this.hasUnsavedInput()) return true;
    return this.injector.get(WorkflowDraftConfirmationService).confirm({
      heading: 'Leave this task?',
      message:
        'Unsaved input in this workspace will be lost, including input in other tasks.',
      confirmText: 'Leave without saving',
      cancelText: 'Stay on page',
    });
  }
  @HostListener('window:beforeunload', ['$event'])
  protectUnsavedInput(event: BeforeUnloadEvent): void {
    if (!this.previewMode && (this.isSaving() || this.hasUnsavedInput())) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  selectedReviewId(): number | null {
    const data = this.state();
    if (!data) return null;
    const occurrence = data.execution.occurrences.find(
      (s) => s.id === data.selectedOccurrenceId,
    );
    const reviews =
      data.execution.reviewHistory?.filter(
        (r) =>
          r.stepCode === occurrence?.stepCode &&
          r.occurrenceNumber === occurrence?.occurrenceNumber,
      ) ?? [];
    return reviews.length
      ? Math.max(...reviews.map((r) => r.decisionRecordId))
      : null;
  }
  displayStatus(code: string | null): string {
    return (code ?? '')
      .replaceAll('_', ' ')
      .replace(/^./, (c) => c.toUpperCase());
  }
  private availableActions(): WorkspaceAction[] {
    const state = this.state(),
      run = state?.execution,
      available = run?.availableActions;
    if (
      !state?.isCurrent ||
      this.isClosed() ||
      !run?.graphExecutionEnabled ||
      !available ||
      available.occurrenceId !== state.selectedOccurrenceId ||
      available.stepETag !== run.stepETag
    )
      return [];
    return available.actions;
  }
  revealNoticeWork(): void {
    const block = this.host.nativeElement.querySelector<HTMLElement>('[data-notice-block="notices.delivery"]');
    block?.scrollIntoView({ behavior:'smooth', block:'start' });
    block?.querySelector<HTMLElement>('button:not(:disabled)')?.focus({ preventScroll:true });
  }
  actions(): WorkspaceAction[] {
    if (this.state()?.nodeTypeCode === 'automated_step') return [];
    return this.availableActions().filter(
      (a) =>
        a.command.key.code !== 'workflow.step.complete' &&
        !isBlockOwnedCommand(a.command.key),
    );
  }
  completeAction(): WorkspaceAction | undefined {
    if (this.state()?.nodeTypeCode === 'automated_step') return undefined;
    const matches = this.availableActions().filter(
      (a) => a.command.key.code === 'workflow.step.complete',
    );
    return matches.length === 1 &&
      workflowCompleteCommandAdapter.matches(matches[0].command)
      ? matches[0]
      : undefined;
  }
  hasCurrentRequirementContext(): boolean {
    const state = this.state(),
      occurrenceId = state?.selectedOccurrenceId,
      workflowId = state?.execution.workflowInstanceId,
      position = state?.context.workflow;
    return (
      !!state?.isCurrent &&
      state.nodeTypeCode === 'human_step' &&
      state.execution.graphExecutionEnabled &&
      Number.isSafeInteger(workflowId) &&
      workflowId! > 0 &&
      Number.isSafeInteger(occurrenceId) &&
      occurrenceId! > 0 &&
      state.execution.activeOccurrenceId === occurrenceId &&
      !!state.nodeId &&
      !!position &&
      position.workflowInstanceId === workflowId &&
      position.definitionVersionId === state.definitionVersionId &&
      position.stepOccurrenceId === occurrenceId &&
      position.nodeId === state.nodeId &&
      state.execution.occurrences.filter(
        (row) =>
          row.id === occurrenceId && row.definitionNodeId === state.nodeId,
      ).length === 1
    );
  }
  async loadCompletionChecklist(generation = this.generation): Promise<void> {
    const state = this.state();
    if (this.previewMode) return;
    if (!state || !this.hasCurrentRequirementContext()) {
      this.completionChecklist.set(null);
      this.checklistLoading.set(false);
      return;
    }
    const occurrenceId = state.selectedOccurrenceId!;
    this.checklistLoading.set(true);
    this.checklistError.set('');
    try {
      const checklist = await firstValueFrom(
        this.http.get<WorkflowCompletionChecklist>(
          this.apis.core.manual.platformWorkflowExecution(
            state.execution.workflowInstanceId,
            '/requirements',
          ),
          { params: { occurrenceId }, headers: this.headers },
        ),
      );
      if (
        generation !== this.generation ||
        this.state()?.selectedOccurrenceId !== occurrenceId
      )
        return;
      if (
        checklist.occurrenceId !== occurrenceId ||
        !Array.isArray(checklist.requirements) ||
        new Set(checklist.requirements.map((r) => r.id)).size !==
          checklist.requirements.length
      )
        throw new Error('The requirement checklist is ambiguous.');
      this.completionChecklist.set(checklist);
      for (const row of checklist.requirements)
        if (row.state === 'satisfied') {
          this.documentFiles.delete(row.id);
          this.documentIntents.delete(row.id);
        }
    } catch (error) {
      if (generation === this.generation) {
        if (
          error instanceof HttpErrorResponse &&
          [401, 403, 404].includes(error.status)
        )
          this.requirementMutationFailed(error, 'Loading the step requirements', '');
        else {
          this.completionChecklist.set(null);
          this.checklistError.set(
            'Current requirement evidence could not be confirmed. Refresh the checklist before continuing.',
          );
        }
      }
    } finally {
      if (generation === this.generation) this.checklistLoading.set(false);
    }
  }
  chooseDocument(event: { id: string; file: File | null }): void {
    if (
      this.previewMode ||
      !this.completeAction() ||
      this.isWaitingForStart() ||
      this.isSaving() ||
      this.visitControlInputPending() ||
      this.loading() ||
      !this.completionChecklist()?.requirements.some(
        (r) => r.id === event.id && r.upload && r.state !== 'satisfied',
      )
    )
      return;
    if (event.file) this.documentFiles.set(event.id, event.file);
    else this.documentFiles.delete(event.id);
    this.documentUploadOutcomes.delete(event.id);
    this.documentIntents.delete(event.id);
    this.error.set('');
    this.confirmation.set('');
  }
  async uploadDocument(id: string): Promise<void> {
    const state = this.state(),
      row = this.completionChecklist()?.requirements.find((r) => r.id === id),
      file = this.documentFiles.get(id);
    if (
      this.previewMode ||
      this.isWaitingForStart() ||
      this.isSaving() ||
      this.visitControlInputPending() ||
      this.loading() ||
      this.reloadRequired() ||
      !state?.isCurrent ||
      !this.completeAction() ||
      !state.selectedOccurrenceId ||
      !state.execution.stepETag ||
      !file ||
      !row?.upload ||
      row.state === 'satisfied'
    )
      return;
    if (!file.size || file.size > row.upload.maximumBytes) {
      this.error.set(
        `Choose a nonempty file up to ${uploadLimitLabel(row.upload.maximumBytes)}.`,
      );
      return;
    }
    const formats = row.upload.allowedFormats;
    const extension =
      file.name.lastIndexOf('.') > 0
        ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
        : '';
    const format =
      extension === 'jpg' || extension === 'jpeg' ? 'jpeg' : extension;
    const contentTypes: Record<string, string> = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpeg: 'image/jpeg',
    };
    const formatMessage = `Choose a ${formats.map((f) => f.toUpperCase()).join(', ')} file for this requirement.`;
    if (
      !formats.includes(format) ||
      !contentTypes[format] ||
      (file.type && file.type.toLowerCase() !== contentTypes[format])
    ) {
      this.error.set(formatMessage);
      return;
    }
    let intent = this.documentIntents.get(id);
    if (
      !intent ||
      intent.file !== file ||
      intent.occurrenceId !== state.selectedOccurrenceId ||
      intent.eTag !== state.execution.stepETag
    ) {
      intent = {
        file,
        occurrenceId: state.selectedOccurrenceId,
        eTag: state.execution.stepETag,
        key: crypto.randomUUID(),
      };
      this.documentIntents.set(id, intent);
    }
    const body = new FormData();
    body.append('occurrenceId', String(state.selectedOccurrenceId));
    body.append('requirementId', id);
    body.append('expectedETag', state.execution.stepETag);
    body.append('file', file);
    const generation = this.generation;
    const attempt = ++this.uploadAttempt;
    this.uploadingDocument.set(id);
    this.documentUploadOutcomes.set(id, {
      state: 'checking',
      message: 'Uploading and checking the file. Not attached yet.',
    });
    this.saving.set(true);
    this.error.set('');
    this.confirmation.set('');
    try {
      const receipt = await firstValueFrom(
        this.http.post<WorkflowDocumentUploadReceipt>(
          this.apis.core.manual.platformWorkflowExecution(
            state.execution.workflowInstanceId,
            '/documents/upload',
          ),
          body,
          { headers: this.headers.set('Idempotency-Key', intent.key) },
        ),
      );
      if (generation !== this.generation) return;
      if (
        !isCleanWorkflowDocumentUploadReceipt(
          receipt,
          state.selectedOccurrenceId,
          id,
        )
      )
        throw new Error('The uploaded document could not be confirmed.');
      this.documentFiles.delete(id);
      this.documentIntents.delete(id);
      this.documentUploadOutcomes.set(id, {
        state: 'clean',
        message: 'Scan passed. Document attached to this visit.',
      });
      this.uploadingDocument.set(null);
      this.session?.invalidate('command-confirmed');
      await this.load();
      if (
        generation + 1 === this.generation &&
        this.state()?.selectedOccurrenceId === state.selectedOccurrenceId &&
        this.documentUploadOutcomes.get(id)?.state === 'clean'
      )
        this.confirmation.set(
          'Document uploaded and scanned for this visit. Complete the step when all requirements are met.',
        );
    } catch (error) {
      if (generation === this.generation) {
        const scanFailure = this.documentScanFailure(error);
        if (scanFailure) {
          this.documentUploadOutcomes.set(id, scanFailure);
          this.error.set(scanFailure.message);
          this.documentIntents.delete(id);
        } else if (error instanceof HttpErrorResponse && error.status === 400) {
          this.documentUploadOutcomes.set(id, {
            state: 'failed',
            message:
              'The document was rejected and was not attached. Check its contents or choose another file.',
          });
          this.error.set(
            `The document was rejected. ${formatMessage} Check its contents or choose another file.`,
          );
          this.documentIntents.delete(id);
        } else {
          this.documentUploadOutcomes.set(id, {
            state: 'failed',
            message:
              'Upload and scan could not be confirmed. The selected file is not confirmed as attached.',
          });
          this.requirementMutationFailed(
            error,
            'Uploading the document',
            'The upload was not confirmed. Your selected file is preserved. Refresh the current state before retrying.',
            false,
          );
        }
      }
    } finally {
      if (attempt === this.uploadAttempt) {
        this.uploadingDocument.set(null);
        this.saving.set(false);
      }
    }
  }
  private documentScanFailure(error: unknown): EzDocumentUploadOutcome | null {
    if (
      !(error instanceof HttpErrorResponse) ||
      ![400, 409].includes(error.status)
    )
      return null;
    const issues: unknown = error.error?.issues;
    if (!Array.isArray(issues)) return null;
    const codes = issues.map((issue) =>
      typeof issue?.code === 'string' ? issue.code : '',
    );
    if (codes.includes('workflow.document.scan_infected'))
      return {
        state: 'infected',
        message:
          'A threat was detected. The document was rejected and was not attached. Choose another file.',
      };
    if (codes.includes('workflow.document.scan_pending'))
      return {
        state: 'pending',
        message:
          'The scan has not completed. The document was not attached. Retry when scanning is available.',
      };
    if (codes.includes('workflow.document.scan_unconfigured'))
      return {
        state: 'unavailable',
        message:
          'File scanning is unavailable. The document was not attached. Contact your administrator or retry later.',
      };
    if (codes.includes('workflow.document.scan_failed'))
      return {
        state: 'failed',
        message:
          'The scan could not finish. The document was not attached. Retry or choose another file.',
      };
    return null;
  }
  canCompleteStep(): boolean {
    const state = this.state(),
      checklist = this.completionChecklist();
    return (
      !this.previewMode &&
      !this.isWaitingForStart() &&
      !this.isSaving() &&
      !this.loading() &&
      !this.checklistLoading() &&
      !this.reloadRequired() &&
      !this.hasUnsavedInput() &&
      !!this.completeAction()?.enabled &&
      !!checklist?.canComplete &&
      checklist.occurrenceId === state?.selectedOccurrenceId
    );
  }
  async completeStep(): Promise<void> {
    const state = this.state();
    if (!state || !this.canCompleteStep()) return;
    const request = workflowCompleteCommandAdapter.request({
      workflowInstanceId: state.execution.workflowInstanceId,
      occurrenceId: state.selectedOccurrenceId,
      stepETag: state.execution.stepETag,
    });
    if (!request) return;
    const signature = JSON.stringify([
      state.execution.workflowInstanceId,
      request.body,
    ]);
    if (this.completionIntent?.signature !== signature)
      this.completionIntent = { signature, key: crypto.randomUUID() };
    const generation = this.generation;
    this.saving.set(true);
    this.error.set('');
    this.confirmation.set('');
    try {
      await firstValueFrom(
        this.http.post(
          this.apis.core.manual.platformWorkflowExecution(
            state.execution.workflowInstanceId,
            request.suffix,
          ),
          request.body,
          {
            headers: this.headers.set(
              'Idempotency-Key',
              this.completionIntent.key,
            ),
          },
        ),
      );
      if (generation !== this.generation) return;
      this.completionIntent = null;
      this.session?.invalidate('command-confirmed');
      await this.load();
      if (generation + 1 === this.generation)
        this.confirmation.set(
          this.state()?.execution.statusCode === 'completed'
            ? 'Step completed. Workflow completed.'
            : 'Step completed.',
        );
    } catch (error) {
      if (generation === this.generation)
        this.requirementMutationFailed(
          error,
          'Completing the step',
          'Completion was not confirmed. Refresh the current state before retrying.',
        );
    } finally {
      this.saving.set(false);
    }
  }
  private requirementMutationFailed(
    error: unknown,
    action: string,
    fallback: string,
    includeServerMessage = true,
  ): void {
    this.session?.invalidate('command-outcome-unknown');
    if (
      error instanceof HttpErrorResponse &&
      [401, 403, 404].includes(error.status)
    ) {
      this.reasonDialog?.close(true);
      this.reasonDialog = null;
      this.accessDenied.set([401, 403].includes(error.status));
      this.state.set(null);
      this.completionChecklist.set(null);
      this.checklistLoading.set(false);
      this.documentFiles.clear();
      this.documentIntents.clear();
      this.documentUploadOutcomes.clear();
      this.uploadingDocument.set(null);
      this.values = {};
      this.issues = {};
      this.taskDrafts.clear();
      this.taskForms.clear();
      this.taskIntents.clear();
      this.recoveredDraft.set('');
      this.session?.dispose();
      this.error.set(this.workspaceActionAccessMessage(error, action));
    } else {
      this.reloadRequired.set(true);
      const message =
        includeServerMessage &&
        error instanceof HttpErrorResponse &&
        typeof error.error?.message === 'string'
          ? error.error.message
          : '';
      this.error.set(message ? `${message} ${fallback}` : fallback);
    }
  }
  private workspaceActionAccessMessage(error: HttpErrorResponse, action: string): string {
    const reason = error.status === 401
      ? 'Your session is no longer active. Sign in again to continue.'
      : error.status === 403
        ? 'Your account does not have access to perform this action on the current visit. If you expect access, ask your access administrator to check the visit’s assignment and permissions.'
        : 'This visit is unavailable. Return to the dashboard and open the current step.';
    const reference: unknown = error.error?.correlationId;
    const support = typeof reference === 'string' && /^[a-zA-Z0-9:._-]{1,120}$/.test(reference)
      ? ` Support reference ${reference}.`
      : '';
    return `${action} failed. ${reason}${support}`;
  }
  action(): WorkspaceAction | undefined {
    const matches = this.actions().filter(
      (a) => workflowCommandIdentity(a.command.key) === this.selectedCommand(),
    );
    return matches.length === 1 ? matches[0] : undefined;
  }
  private preferredAction(): WorkspaceAction | undefined {
    const actions = this.actions(),
      interaction = this.state()?.detail.mainInteraction;
    const main = interaction
      ? workflowCommandIdentity(interaction.command.key)
      : null;
    return (
      actions.find(
        (action) =>
          action.enabled &&
          workflowCommandIdentity(action.command.key) === main,
      ) ??
      actions.find((action) => action.enabled) ??
      actions[0]
    );
  }
  private readonly commandBinding = computed(() => {
    const action = this.action(),
      interaction = this.state()?.detail.mainInteraction;
    if (!action) return null;
    // The main interaction describes one command, not every authorized task on the step.
    // Matching by identity first keeps a stale hash or unsupported main renderer fail-closed.
    const isMain =
      interaction &&
      workflowCommandIdentity(interaction.command.key) ===
        workflowCommandIdentity(action.command.key);
    return this.resolveCommand(action, isMain ? interaction : null);
  });
  readonly form = computed<GeneratedFormDefinition | null>(
    () => this.commandBinding()?.form ?? null,
  );
  private selectedOutcome() {
    const field = this.commandBinding()?.adapter.outcomeField;
    return field
      ? this.action()?.outcomes.find(
          (outcome) => outcome.code === this.values[field],
        )
      : undefined;
  }
  /** Review v1 owns text in notes. Additive metadata cannot replace that retained contract with a code-only reason. */
  reviewReasonPresentation(): WorkflowReasonCapturePresentation | null {
    const action = this.action(),
      key = action?.command.key,
      outcome = this.selectedOutcome();
    const field = this.form()?.fields.find((item) => item.name === 'notes');
    if (
      !action ||
      key?.provider !== 'workflow' ||
      key.version !== 1 ||
      !['workflow.review.submit', 'workflow.review.record'].includes(
        key.code,
      ) ||
      action.form?.contract !== 'WorkflowReviewSubmissionRequest' ||
      !outcome ||
      !field ||
      field.readOnly ||
      !['text', 'textarea'].includes(field.control) ||
      this.isDetailAuthoring
    )
      return null;
    const expected = reviewReasonCaptureContract(
      outcome.code === 'changes_required' ||
        outcome.reasonPolicy === 'required',
    );
    const supplied = outcome.reasonCapture;
    const valid =
      supplied === undefined ||
      supplied === null ||
      (isWorkflowReasonCaptureContract(supplied) &&
        supplied.contractCode === expected.contractCode &&
        supplied.purpose === 'review' &&
        supplied.mode === 'text' &&
        supplied.textField === 'notes' &&
        supplied.codeField === null &&
        supplied.codeRequired === false &&
        supplied.textRequired === expected.textRequired &&
        supplied.textMaxLength === expected.textMaxLength &&
        !supplied.permittedAdministratorConfiguration.length);
    return {
      contract: {
        ...(valid && supplied ? supplied : expected),
        textRequired:
          expected.textRequired ||
          field.required ||
          (!!field.requiredWhen &&
            this.values[field.requiredWhen.field] ===
              field.requiredWhen.equals),
        textMaxLength: Math.min(
          expected.textMaxLength!,
          field.maxLength ?? expected.textMaxLength!,
        ),
      },
      choices: [],
      textLabel: field.label || 'Review notes',
      ...(valid
        ? {}
        : {
            unavailable:
              'The registered reason requirements are unavailable. Refresh current state.',
          }),
    };
  }
  canEditReviewReason(): boolean {
    return (
      !!this.reviewReasonPresentation() &&
      !this.reviewReasonPresentation()?.unavailable &&
      !this.isSaving() &&
      !this.loading() &&
      !this.reloadRequired() &&
      !this.visitControlInputPending() &&
      !(this.previewMode && this.previewReadOnly) &&
      !!this.action()?.enabled &&
      !!this.selectedOutcome()?.enabled &&
      this.isEditableVisit()
    );
  }
  editReviewReason(): void {
    const presentation = this.reviewReasonPresentation();
    if (!presentation || !this.canEditReviewReason() || this.reasonDialog)
      return;
    const generation = this.generation,
      identity = this.selectedCommand(),
      outcome = this.values['outcome'],
      profile = JSON.stringify(presentation);
    const handle = this.reasonDialogs.open({
      heading: `${this.selectedOutcome()?.label || 'Review'} · Reason`,
      presentation,
      value: { code: '', text: String(this.values['notes'] ?? '') },
      isCurrent: () =>
        generation === this.generation &&
        identity === this.selectedCommand() &&
        outcome === this.values['outcome'] &&
        profile === JSON.stringify(this.reviewReasonPresentation()) &&
        this.canEditReviewReason(),
      valueChange: (value) => this.updateValues({ notes: value.text }),
    });
    this.reasonDialog = handle;
    void handle.closed.finally(() => {
      if (this.reasonDialog === handle) this.reasonDialog = null;
    });
  }
  private commandRequest() {
    const state = this.state(),
      action = this.action(),
      binding = this.commandBinding();
    return state && action && binding
      ? this.commandAdapters.request(
          binding,
          action,
          {
            workflowInstanceId: state.execution.workflowInstanceId,
            occurrenceId: state.selectedOccurrenceId,
            stepETag: state.execution.stepETag,
          },
          this.values,
          this.fieldPolicySource(),
        )
      : null;
  }
  updateValues(value: GeneratedFormValues): void {
    if (this.previewMode && (this.previewReadOnly || this.displayOnlyPreviewForm())) return;
    // ui-form may emit internal proxy metadata. Only declared fields are user input.
    const fields = this.form()?.fields ?? [];
    value = Object.fromEntries(
      Object.entries(value).filter(([key]) =>
        fields.some((field) => field.name === key),
      ),
    );
    if (
      Object.entries(value).some(
        ([key, entry]) => (this.values[key] ?? '') !== (entry ?? ''),
      )
    )
      this.confirmation.set('');
    // Individual controls can emit before the form has received the previous input update.
    const action = this.action();
    const next = { ...this.editableInput(this.values), ...value };
    this.values = action
      ? this.commandAdapters.withBoundValues(action, next)
      : next;
    this.issues = {};
    this.emitPreviewInput();
  }
  choose(action: WorkspaceAction): void {
    const identity = workflowCommandIdentity(action.command.key);
    if (this.isSaving() || identity === this.selectedCommand()) return;
    const current = this.actions().filter(item => workflowCommandIdentity(item.command.key) === identity);
    if ((action.form?.captureForm?.version === 3 || current.some(item => item.form?.captureForm?.version === 3))
      && (current.length !== 1 || JSON.stringify(current[0]) !== JSON.stringify(action))) return;
    this.reasonDialog?.close();
    this.reasonDialog = null;
    this.taskDrafts.set(
      this.selectedCommand(),
      this.editableInput(this.values),
    );
    if (this.intent) this.taskIntents.set(this.selectedCommand(), this.intent);
    this.values = { ...this.taskDrafts.get(identity) };
    this.selectedCommand.set(identity);
    this.initializeTaskInput(action);
    this.issues = {};
    this.intent = this.taskIntents.get(identity) ?? null;
    this.confirmation.set('');
    this.emitPreviewInput();
  }
  /** Read local sample inputs without changing the selected task or exposing live staff drafts. */
  previewDraftValues(
    command: WorkspaceAction['command'],
  ): GeneratedFormValues | null {
    if (!this.previewMode) return null;
    const identity = workflowCommandIdentity(command.key),
      matches = this.actions().filter(
        (action) => workflowCommandIdentity(action.command.key) === identity,
      );
    if (
      matches.length !== 1 ||
      matches[0].command.inputSchemaHash !== command.inputSchemaHash ||
      matches[0].command.contractHash !== command.contractHash
    )
      return null;
    return structuredClone(
      identity === this.selectedCommand()
        ? this.values
        : (this.taskDrafts.get(identity) ?? {}),
    );
  }
  outcomeReasons(): string[] {
    return (
      this.selectedOutcome()?.blockReasons.map((reason) => reason.message) ?? []
    );
  }
  stepRequirementMessages(): string[] {
    const represented = new Set(this.completionChecklist()?.requirements.flatMap(row => [row.message, row.label]) ?? []);
    const messages = [
      ...(this.completeAction()?.blockReasons.map(reason => reason.message) ?? []),
      ...(!this.selectedFormSaved() && !this.selectedDecisionRecorded() ? [
        ...(this.action()?.blockReasons.map(reason => reason.message) ?? []), ...this.outcomeReasons(),
      ] : []),
    ];
    return [...new Set(messages)].filter(message => message && !represented.has(message));
  }
  requirements(): Requirement[] {
    return this.selectedOutcome()?.requirements ?? [];
  }
  private inputProvided(check: Requirement): boolean {
    const field = check.inputField;
    return (
      check.state === 'input_required' &&
      !!field &&
      !!this.form()?.fields.some((f) => f.name === field) &&
      String(this.values[field] ?? '').trim().length > 0 &&
      !this.clientIssues()[field]
    );
  }
  requirementLabel(check: Requirement): string {
    return this.inputProvided(check)
      ? 'Input provided; checked on save'
      : this.displayStatus(check.state);
  }
  private needsAttention(check: Requirement): boolean {
    return (
      !['satisfied', 'not_applicable', 'preview'].includes(check.state) &&
      !this.inputProvided(check)
    );
  }
  requirementsNeedAttention(): boolean {
    return this.requirements().some((check) => this.needsAttention(check));
  }
  requirementsSummary(): string {
    const checks = this.requirements();
    if (!this.state()?.isCurrent) return 'Read-only visit';
    if (!checks.length)
      return this.action()?.outcomes.length
        ? 'Choose an outcome'
        : 'Checked on submission';
    const attention = checks.filter((check) =>
      this.needsAttention(check),
    ).length;
    return attention
      ? `${attention} ${attention === 1 ? 'needs' : 'need'} attention`
      : `${checks.length} ${checks.length === 1 ? 'check' : 'checks'} · ${this.previewMode ? 'Preview' : 'Ready'}`;
  }
  private canAttemptSubmission(): boolean {
    const action = this.action();
    return (
      !this.previewMode &&
      !this.isSaving() &&
      !this.visitControlInputPending() &&
      !this.loading() &&
      !this.reloadRequired() &&
      !this.selectedFormSaved() &&
      !this.selectedDecisionRecorded() &&
      !!this.form() &&
      !!this.commandRequest() &&
      !!action?.enabled &&
      (!action.outcomes.length || !!this.selectedOutcome()?.enabled)
    );
  }
  clientIssues(): FormIssues {
    const binding = this.commandBinding(),
      action = this.action(),
      reason = this.reviewReasonPresentation();
    if (!binding || !action) return {};
    return {
      ...validateGeneratedForm(binding.form, this.values),
      ...activeCaptureLookupIssues(binding.form, binding.lookupIssues ?? {}, this.values),
      ...(reason
        ? reasonCaptureIssues(reason, {
            code: '',
            text: String(this.values['notes'] ?? ''),
          })
        : {}),
      ...binding.adapter.validateInput?.(action, this.values),
    };
  }
  canSubmit(): boolean {
    return (
      this.canAttemptSubmission() &&
      Object.keys(this.clientIssues()).length === 0
    );
  }
  saveLabel(): string {
    return this.isSaving()
      ? 'Saving…'
      : (this.commandBinding()?.adapter.submitLabel ?? 'Submit task');
  }
  submissionHint(): string {
    if (this.previewMode) return 'Preview only. Nothing can be saved.';
    if (this.isSaving()) return 'Saving your work…';
    if (this.visitControlInputPending())
      return 'Save or cancel your changes before submitting another task.';
    if (this.reloadRequired())
      return 'Refresh the current state before trying again. Your input is preserved.';
    const action = this.action();
    if (!action?.enabled)
      return (
        action?.blockReasons[0]?.message ||
        'This task is currently unavailable.'
      );
    if (!this.commandBinding())
      return 'A compatible task form is not installed.';
    if (action.outcomes.length && !this.selectedOutcome())
      return (
        this.commandBinding()?.adapter.outcomePrompt ??
        'Choose an outcome to continue.'
      );
    const outcome = this.selectedOutcome();
    if (outcome && !outcome.enabled)
      return (
        outcome.blockReasons[0]?.message ||
        'This outcome is currently unavailable.'
      );
    const issue = Object.entries(this.clientIssues())[0];
    if (issue)
      return `${this.form()?.fields.find((field) => field.name === issue[0])?.label || issue[0]}: ${issue[1]}`;
    if (!this.commandRequest())
      return 'Current task context is incomplete. Reload before submitting.';
    return (
      this.commandBinding()?.adapter.submissionHint ??
      'Submit the configured task.'
    );
  }
  async submit(): Promise<void> {
    const state = this.state(),
      action = this.action(),
      form = this.form();
    if (!state || !action || !form || !this.canAttemptSubmission()) return;
    this.issues = this.clientIssues();
    if (Object.keys(this.issues).length) return;
    this.reasonDialog?.close();
    this.reasonDialog = null;
    const request = this.commandRequest(),
      binding = this.commandBinding();
    if (!request || !binding) return;
    const body = request.body;
    const url = this.apis.core.manual.platformWorkflowExecution(
      state.execution.workflowInstanceId,
      request.suffix,
    );
    const signature = JSON.stringify([url, body]);
    if (this.intent?.signature !== signature)
      this.intent = { signature, key: crypto.randomUUID() };
    const generation = this.generation, authority = this.runtimeAuthorityKey();
    this.saving.set(true);
    this.error.set('');
    this.confirmation.set('');
    try {
      await firstValueFrom(
        this.http.post(url, body, {
          headers: this.headers.set('Idempotency-Key', this.intent.key),
        }),
      );
      if (generation !== this.generation || authority !== this.runtimeAuthorityKey()) return;
      this.intent = null;
      this.taskDrafts.delete(workflowCommandIdentity(action.command.key));
      this.taskIntents.delete(workflowCommandIdentity(action.command.key));
      this.values = {};
      this.issues = {};
      this.session?.invalidate('command-confirmed');
      this.saving.set(false);
      await this.load();
      if (generation + 1 !== this.generation || authority !== this.runtimeAuthorityKey()) return;
      const fresh = this.state();
      const saved = binding.adapter.confirmation;
      this.confirmation.set(
        saved +
          (fresh?.execution.statusCode === 'completed'
            ? ' Workflow completed.'
            : fresh &&
                fresh.execution.activeOccurrenceId !==
                  state.execution.activeOccurrenceId
              ? ` Workflow advanced to ${fresh.stepLabel || this.displayStatus(fresh.execution.currentStepCode || 'the next step')}.`
              : fresh
                ? ''
                : ' Refresh to load the current work.'),
      );
    } catch (error) {
      if (generation !== this.generation || authority !== this.runtimeAuthorityKey()) return;
      this.session?.invalidate('command-outcome-unknown');
      if (
        error instanceof HttpErrorResponse &&
        [401, 403, 404].includes(error.status)
      ) {
        this.accessDenied.set([401, 403].includes(error.status));
        this.state.set(null);
        this.completionChecklist.set(null);
        this.checklistLoading.set(false);
        this.checklistError.set('');
        this.ledger.set(null);
        this.ledgerState.set('unavailable');
        this.confirmation.set('');
        this.intent = null;
        this.values = {};
        this.issues = {};
        this.taskDrafts.clear();
        this.documentFiles.clear();
        this.documentIntents.clear();
        this.documentUploadOutcomes.clear();
        this.uploadingDocument.set(null);
        this.taskForms.clear();
        this.taskIntents.clear();
        this.recoveredDraft.set('');
        this.session?.dispose();
        this.error.set(this.workspaceActionAccessMessage(error, 'Submitting the action'));
        if (error.status === 403) {
          // A denied command does not establish read access. Discard its protected
          // input before asking the workspace endpoint for current authority.
          this.saving.set(false);
          await this.load();
          if (generation + 1 === this.generation && authority === this.runtimeAuthorityKey() && this.state())
            this.error.set(
              'The submission was denied. Current work has been refreshed. Review the available actions before continuing.',
            );
        }
      } else {
        this.issues = serverFormIssues(
          form,
          error instanceof HttpErrorResponse ? (error.error ?? {}) : {},
        );
        this.reloadRequired.set(true);
        this.error.set(
          'The submission was not confirmed. Your input is preserved. Reload the current state before trying again.',
        );
        if (
          error instanceof HttpErrorResponse &&
          [409, 412].includes(error.status)
        ) {
          this.saving.set(false);
          await this.load();
          if (generation + 1 === this.generation && authority === this.runtimeAuthorityKey() && this.state())
            this.error.set(
              'The submission was not confirmed. Current work has been refreshed. Review it before trying again. Your input is preserved.',
            );
        }
      }
    } finally {
      if (generation === this.generation && authority === this.runtimeAuthorityKey()) this.saving.set(false);
    }
  }
  private runtimeAuthorityKey(): string {
    const profile = this.roles.accessProfile?.();
    if (!profile) return 'unresolved';
    const scopeKeys = (scopes: readonly { scopeTypeCode: string; scopeKey?: string | null }[] = []) => [...new Set(scopes.map(scope => JSON.stringify([scope.scopeTypeCode, scope.scopeKey ?? null])))].sort();
    return JSON.stringify([
      profile.actor.actorId, profile.actor.userId, profile.actor.providerCode,
      [...new Set((profile.permissions ?? []).map(permission => JSON.stringify([permission.code, permission.scopeRequired, scopeKeys(permission.scopes)])))].sort(),
      [...new Set((profile.roles ?? []).map(role => JSON.stringify([role.code, scopeKeys(role.scopes)])))].sort(),
      scopeKeys(profile.scopes),
    ]);
  }
  private invalidateChangedAuthority(): void {
    this.generation++;
    this.automationRecoveryAvailable.set(false);
    this.reasonDialog?.close(true); this.reasonDialog = null;
    this.session?.dispose(); this.session = undefined;
    this.state.set(null); this.completionChecklist.set(null); this.ledger.set(null); this.ledgerState.set('unavailable');
    this.mainBlocks = []; this.supportingBlocks = [];
    this.values = {}; this.issues = {}; this.intent = null;
    this.taskDrafts.clear(); this.taskForms.clear(); this.taskIntents.clear();
    this.documentFiles.clear(); this.documentIntents.clear(); this.documentUploadOutcomes.clear(); this.uploadingDocument.set(null);
    this.confirmation.set(''); this.recoveredDraft.set(''); this.loading.set(false); this.saving.set(false);
    this.reloadRequired.set(true); this.error.set('Current access changed. Reload to check this workspace before continuing.');
  }
  lookupStatusRows(): { id: string; label: string; message: string; signature: string; canClear: boolean }[] {
    const binding = this.commandBinding();
    if (!binding) return [];
    return visibleGeneratedFields(binding.form, this.values).filter(field => binding.lookupIssues?.[field.name]).map(field => ({
      id: field.name, label: this.formWording(binding.form).definition.fields.find(item => item.name === field.name)?.label ?? field.label,
      message: binding.lookupIssues![field.name],
      signature: JSON.stringify([this.runtimeAuthorityKey(), this.fieldPolicySource(), this.selectedCommand(), binding.lookupKey, binding.fieldPolicyKey, field.name, this.values[field.name]]),
      canClear: !field.required && !field.readOnly && this.values[field.name] !== undefined && this.values[field.name] !== null && this.values[field.name] !== ''
        && !this.previewMode && !this.isSaving() && !this.loading() && !this.reloadRequired() && !this.visitControlInputPending()
        && !this.reasonDialog && !this.ownerViews?.focusSuspended() && !!this.action()?.enabled && this.isEditableVisit() && this.formOnActiveTab(),
    }));
  }
  trackLookupStatus(_index: number, row: { id: string }): string { return row.id; }
  clearLookupValue(id: string, signature: string): void {
    const row = this.lookupStatusRows().find(item => item.id === id);
    if (!row?.canClear || row.signature !== signature) return;
    const generation = this.generation, authority = this.runtimeAuthorityKey(), identity = this.selectedCommand(), key = this.commandBinding()?.lookupKey;
    const previousIssues = this.issues;
    this.updateValues({ [id]: '' });
    this.issues = Object.fromEntries(Object.entries(previousIssues).filter(([name]) => name !== id));
    setTimeout(() => {
      if (generation !== this.generation || authority !== this.runtimeAuthorityKey() || identity !== this.selectedCommand()
        || key !== this.commandBinding()?.lookupKey || this.values[id] !== '' || this.reasonDialog || this.ownerViews?.focusSuspended()
        || !this.host.nativeElement.isConnected || this.host.nativeElement.closest('[hidden], [inert]')) return;
      const target = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[data-lookup-field]')).find(element => element.dataset['lookupField'] === id);
      if (!target || target.closest('[hidden], [inert]') || !target.getClientRects().length) return;
      target.focus(); target.scrollIntoView?.({ block: 'nearest' });
    });
  }
  ngOnDestroy(): void {
    this.pageIdentity.clear(this);
    this.reasonDialog?.close(true);
    this.reasonDialog = null;
    this.generation++;
    this.session?.dispose();
  }
}
